import { useState, type CSSProperties } from "react";
import OperationsStudioTabs from "../../components/admin/OperationsStudioTabs";
import { useIsMobile } from "../../lib/useIsMobile";

export default function AdminVoice() {
  // <1024 (no desktop rail): the form + sandbox columns stack — at 768 they were 781px wide.
  // <768: the 3 routing cards and the number fields stack too.
  const isNarrow = useIsMobile(1024);
  const isPhone = useIsMobile(768);
  const [phone, setPhone] = useState("+1 (312) 555-0199");
  const [forwardNumber, setForwardNumber] = useState("+1 (312) 555-0188");
  const [routingMode, setRoutingMode] = useState<"ai_concierge" | "softphone" | "voicemail">("ai_concierge");
  const [voicePersona, setVoicePersona] = useState<"sophisticated" | "concise" | "sommelier">("sophisticated");
  const [systemPrompt, setSystemPrompt] = useState(
    "You are the sophisticated AI Concierge for The Sentinel Room, an exclusive private speakeasy and whiskey library in Chicago. You speak with warm elegance, refined hospitality, and extensive knowledge of rare bourbons and single malts. You assist callers with directions, dress code (smart casual / tailored evening wear), member hours, walk-in availability, and private event reservations."
  );
  const [voicemailGreeting, setVoicemailGreeting] = useState(
    "Thank you for calling The Sentinel Room. We are currently curated for private evening service. Please leave your name, phone number, and party size, and our Maître D' will return your call promptly."
  );
  const [smsKeyword, setSmsKeyword] = useState("SENTINEL");
  const [success, setSuccess] = useState(false);

  // Test Simulator State
  const [testQuery, setTestQuery] = useState("");
  const [testConversation, setTestConversation] = useState<Array<{ role: "caller" | "ai"; text: string }>>([
    { role: "ai", text: "Good evening, Sentinel Room. How may I assist your private evening service tonight?" }
  ]);
  const [simulating, setSimulating] = useState(false);

  function handleSave() {
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  }

  function handleApplyPreset(preset: "sophisticated" | "concise" | "sommelier") {
    setVoicePersona(preset);
    if (preset === "sophisticated") {
      setSystemPrompt(
        "You are the sophisticated AI Concierge for The Sentinel Room, an exclusive private speakeasy and whiskey library in Chicago. You speak with warm elegance, refined hospitality, and extensive knowledge of rare bourbons and single malts. You assist callers with directions, dress code, member hours, walk-in availability, and private event reservations."
      );
    } else if (preset === "concise") {
      setSystemPrompt(
        "You are the fast, professional voice receptionist for The Sentinel Room. Provide brief, courteous, direct answers to hours, dress code, directions, and walk-in policies. Offer to transfer urgent VIP requests directly to the floor manager."
      );
    } else if (preset === "sommelier") {
      setSystemPrompt(
        "You are the Master Sommelier and Spirits Curator AI for The Sentinel Room. You provide encyclopedic knowledge of the 500+ bottle library, single barrel picks, Pappy Van Winkle allocations, and C33 VIP membership perks."
      );
    }
  }

  function handleSimulate() {
    if (!testQuery.trim()) return;
    const q = testQuery.trim();
    setTestQuery("");
    setTestConversation(prev => [...prev, { role: "caller", text: q }]);
    setSimulating(true);

    setTimeout(() => {
      let aiReply = "Our speakeasy doors open at 5:00 PM tonight. We welcome walk-ins at our standing library rail, though reservations for private booths are recommended through our host stand.";
      if (q.toLowerCase().includes("dress") || q.toLowerCase().includes("wear")) {
        aiReply = "Our dress code is smart casual to tailored evening wear. We kindly request guests refrain from athletic apparel, flip-flops, or baseball caps.";
      } else if (q.toLowerCase().includes("whiskey") || q.toLowerCase().includes("bourbon") || q.toLowerCase().includes("pappy")) {
        aiReply = "Our library curates over 500 rare expressions, including vintage Stitzel-Weller bottlings, BTAC releases, and exclusive C33 single-barrel selections available neat or in custom tasting flights.";
      } else if (q.toLowerCase().includes("member") || q.toLowerCase().includes("c33") || q.toLowerCase().includes("join")) {
        aiReply = "The Century 33 VIP Society includes 12 complimentary rare curator pours, private bottle locker storage, and priority booking. Applications are reviewed quarterly.";
      }
      setTestConversation(prev => [...prev, { role: "ai", text: aiReply }]);
      setSimulating(false);
    }, 600);
  }

  return (
    <div style={styles.container}>
      <OperationsStudioTabs activeTab="settings" />

      <header style={styles.header}>
        <h1 style={styles.title}>Voice AI Receptionist &amp; IVR Routing</h1>
        <p style={styles.subtitle}>
          Configure Twilio voice routing, AI concierge prompts, SMS keywords, and interactive call testing.
        </p>
      </header>

      {success && <div style={styles.successBanner}>✓ Voice AI settings updated and synced to Twilio!</div>}

      <div style={isNarrow ? { ...styles.grid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.grid}>
        {/* Left Column: Configuration Settings */}
        <div style={styles.formPanel}>
          {/* Answering Mode Selector */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Inbound Voice Routing Mode</h2>
            <div style={isPhone ? { ...styles.routingGrid, gridTemplateColumns: "minmax(0, 1fr)" } : styles.routingGrid}>
              <div
                style={{
                  ...styles.routingCard,
                  ...(routingMode === "ai_concierge" ? styles.routingCardActive : {}),
                }}
                onClick={() => setRoutingMode("ai_concierge")}
              >
                <div style={styles.routingIcon}>🤖</div>
                <div style={styles.routingName}>AI Concierge</div>
                <div style={styles.routingDesc}>Conversational AI answers, handles FAQs, and logs transcripts</div>
              </div>

              <div
                style={{
                  ...styles.routingCard,
                  ...(routingMode === "softphone" ? styles.routingCardActive : {}),
                }}
                onClick={() => setRoutingMode("softphone")}
              >
                <div style={styles.routingIcon}>📞</div>
                <div style={styles.routingName}>Live Softphone Ring</div>
                <div style={styles.routingDesc}>Direct ring to manager tablets &amp; desktop softphones</div>
              </div>

              <div
                style={{
                  ...styles.routingCard,
                  ...(routingMode === "voicemail" ? styles.routingCardActive : {}),
                }}
                onClick={() => setRoutingMode("voicemail")}
              >
                <div style={styles.routingIcon}>📼</div>
                <div style={styles.routingName}>Voicemail IVR</div>
                <div style={styles.routingDesc}>Plays curated greeting and records audio voicemail</div>
              </div>
            </div>
          </div>

          {/* AI Persona & System Instructions */}
          {routingMode === "ai_concierge" && (
            <div style={styles.formSection}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, ...(isPhone ? { flexWrap: "wrap", gap: 8 } : {}) }}>
                <h2 style={{ ...styles.sectionTitle, margin: 0 }}>AI Voice Persona &amp; Instructions</h2>
                <div style={styles.presetGroup}>
                  <button
                    type="button"
                    style={{ ...styles.presetBtn, ...(voicePersona === "sophisticated" ? styles.presetBtnActive : {}) }}
                    onClick={() => handleApplyPreset("sophisticated")}
                  >
                    Speakeasy Concierge
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.presetBtn, ...(voicePersona === "concise" ? styles.presetBtnActive : {}) }}
                    onClick={() => handleApplyPreset("concise")}
                  >
                    Concise &amp; Fast
                  </button>
                  <button
                    type="button"
                    style={{ ...styles.presetBtn, ...(voicePersona === "sommelier" ? styles.presetBtnActive : {}) }}
                    onClick={() => handleApplyPreset("sommelier")}
                  >
                    Spirits Curator
                  </button>
                </div>
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.label}>System Instructions &amp; Speakeasy Guardrails</label>
                <textarea
                  value={systemPrompt}
                  onChange={(e) => setSystemPrompt(e.target.value)}
                  style={{ ...styles.textarea, minHeight: 120 }}
                />
              </div>
            </div>
          )}

          {/* Voicemail Greeting */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Automated Voicemail Greeting</h2>
            <div style={styles.inputGroup}>
              <label style={styles.label}>Text-to-Speech Greeting Message</label>
              <textarea
                value={voicemailGreeting}
                onChange={(e) => setVoicemailGreeting(e.target.value)}
                style={styles.textarea}
              />
            </div>
          </div>

          {/* Escalation & Numbers */}
          <div style={styles.formSection}>
            <h2 style={styles.sectionTitle}>Telephony &amp; SMS Forwarding</h2>
            <div style={isPhone ? { ...styles.twoCol, gridTemplateColumns: "minmax(0, 1fr)" } : styles.twoCol}>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Twilio Primary Inbound Line</label>
                <input
                  type="text"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  style={styles.input}
                />
              </div>
              <div style={styles.inputGroup}>
                <label style={styles.label}>Manager Escalation Cell (Urgent SMS)</label>
                <input
                  type="text"
                  value={forwardNumber}
                  onChange={(e) => setForwardNumber(e.target.value)}
                  style={styles.input}
                />
              </div>
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Default MFA &amp; SMS Keyword</label>
              <input
                type="text"
                value={smsKeyword}
                onChange={(e) => setSmsKeyword(e.target.value)}
                style={styles.input}
              />
            </div>
          </div>

          <button onClick={handleSave} style={styles.saveBtn}>Save Telephony &amp; AI Settings</button>
        </div>

        {/* Right Column: Interactive Voice Simulator */}
        <div style={styles.simulatorPanel}>
          <div style={styles.simulatorHeader}>
            <span style={styles.simBadge}>⚡ LIVE TESTING SANDBOX</span>
            <h3 style={styles.simTitle}>Voice AI Simulator</h3>
            <p style={styles.simSubtitle}>Test caller dialogues before publishing to the live telephone network.</p>
          </div>

          <div style={styles.chatBox}>
            {testConversation.map((msg, i) => (
              <div
                key={i}
                style={{
                  ...styles.chatBubble,
                  ...(msg.role === "caller" ? styles.chatCaller : styles.chatAi),
                }}
              >
                <span style={styles.chatRole}>{msg.role === "caller" ? "👤 Caller" : "🤖 Sentinel AI"}</span>
                <div>{msg.text}</div>
              </div>
            ))}
            {simulating && (
              <div style={{ ...styles.chatBubble, ...styles.chatAi, color: "var(--gold, #F9AF36)" }}>
                <em>AI Concierge synthesizing voice reply...</em>
              </div>
            )}
          </div>

          <div style={styles.simInputRow}>
            <input
              type="text"
              placeholder="Type simulated guest question (e.g. 'What is the dress code?')"
              value={testQuery}
              onChange={(e) => setTestQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSimulate()}
              style={styles.simInput}
            />
            <button
              type="button"
              onClick={handleSimulate}
              disabled={simulating || !testQuery.trim()}
              style={styles.simBtn}
            >
              Test
            </button>
          </div>

          <div style={styles.sampleQuestions}>
            <span style={styles.sampleLabel}>Suggested Tests:</span>
            <button type="button" onClick={() => { setTestQuery("What is the dress code tonight?"); }} style={styles.sampleChip}>
              Dress Code
            </button>
            <button type="button" onClick={() => { setTestQuery("Do you have Pappy Van Winkle or rare bourbons?"); }} style={styles.sampleChip}>
              Rare Bourbons
            </button>
            <button type="button" onClick={() => { setTestQuery("How do I become a C33 VIP member?"); }} style={styles.sampleChip}>
              VIP Membership
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    maxWidth: 1200,
    margin: "0 auto",
    padding: "24px 20px",
  },
  header: { marginBottom: 20 },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 24,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 4px",
  },
  subtitle: {
    color: "var(--muted, #8A7A66)",
    fontSize: 13,
    margin: 0,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
    gap: 20,
    alignItems: "start",
  },
  formPanel: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    padding: 20,
  },
  formSection: {
    marginBottom: 20,
    borderBottom: "1px solid var(--border, #231D19)",
    paddingBottom: 16,
  },
  sectionTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 15,
    color: "var(--gold, #F9AF36)",
    margin: "0 0 12px",
  },
  routingGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr",
    gap: 10,
  },
  routingCard: {
    background: "rgba(0,0,0,0.3)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: 12,
    cursor: "pointer",
    textAlign: "center",
    transition: "all 0.15s ease",
  },
  routingCardActive: {
    background: "rgba(249, 175, 54, 0.12)",
    border: "1px solid var(--gold, #F9AF36)",
  },
  routingIcon: { fontSize: 22, marginBottom: 4 },
  routingName: { fontSize: 12, fontWeight: 700, color: "var(--text, #F0E6D4)", marginBottom: 4 },
  routingDesc: { fontSize: 10, color: "var(--muted, #8A7A66)", lineHeight: 1.3 },
  presetGroup: {
    display: "flex",
    gap: 6,
  },
  presetBtn: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 4,
    color: "var(--muted, #8A7A66)",
    fontSize: 10,
    padding: "3px 6px",
    cursor: "pointer",
    fontWeight: 600,
  },
  presetBtnActive: {
    background: "rgba(249, 175, 54, 0.2)",
    border: "1px solid var(--gold, #F9AF36)",
    color: "var(--gold, #F9AF36)",
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
  },
  inputGroup: {
    marginBottom: 12,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    fontSize: 10,
    fontWeight: 700,
    color: "var(--muted, #8A7A66)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    padding: "8px 12px",
    color: "var(--text, #F0E6D4)",
    fontSize: 13,
    outline: "none",
  },
  textarea: {
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    padding: "10px 12px",
    color: "var(--text, #F0E6D4)",
    fontSize: 12,
    lineHeight: 1.5,
    outline: "none",
    minHeight: 80,
    resize: "vertical",
    fontFamily: "inherit",
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 6,
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: "bold",
    textTransform: "uppercase",
    cursor: "pointer",
    letterSpacing: "0.5px",
    width: "100%",
  },
  successBanner: {
    background: "rgba(39, 174, 96, 0.15)",
    border: "1px solid rgba(39, 174, 96, 0.4)",
    color: "#4ade80",
    borderRadius: 6,
    padding: "10px 14px",
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 16,
  },
  simulatorPanel: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 10,
    padding: 20,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    boxSizing: "border-box",
  },
  simulatorHeader: {
    borderBottom: "1px solid var(--border, #231D19)",
    paddingBottom: 12,
    marginBottom: 14,
  },
  simBadge: {
    fontSize: 9,
    fontWeight: 800,
    color: "var(--gold, #F9AF36)",
    letterSpacing: 1,
  },
  simTitle: {
    fontSize: 16,
    fontFamily: "Georgia, serif",
    color: "var(--text, #F0E6D4)",
    margin: "4px 0 2px",
  },
  simSubtitle: {
    fontSize: 11,
    color: "var(--muted, #8A7A66)",
    margin: 0,
  },
  chatBox: {
    flex: 1,
    minHeight: 280,
    maxHeight: 380,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 10,
    padding: 8,
    background: "rgba(0,0,0,0.25)",
    borderRadius: 8,
    border: "1px solid var(--border, #231D19)",
    marginBottom: 12,
  },
  chatBubble: {
    padding: "8px 12px",
    borderRadius: 8,
    fontSize: 12,
    lineHeight: 1.4,
    maxWidth: "85%",
  },
  chatCaller: {
    alignSelf: "flex-end",
    background: "rgba(249, 175, 54, 0.15)",
    border: "1px solid rgba(249, 175, 54, 0.3)",
    color: "var(--text, #F0E6D4)",
  },
  chatAi: {
    alignSelf: "flex-start",
    background: "rgba(255,255,255,0.05)",
    border: "1px solid var(--border, #231D19)",
    color: "var(--text, #F0E6D4)",
  },
  chatRole: {
    display: "block",
    fontSize: 9,
    fontWeight: 700,
    textTransform: "uppercase",
    marginBottom: 2,
    color: "var(--muted, #8A7A66)",
  },
  simInputRow: {
    display: "flex",
    gap: 8,
    marginBottom: 10,
  },
  simInput: {
    flex: 1,
    background: "var(--bg, #0D0B08)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 6,
    padding: "8px 12px",
    color: "var(--text, #F0E6D4)",
    fontSize: 12,
    outline: "none",
  },
  simBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#111",
    border: 0,
    borderRadius: 6,
    padding: "8px 14px",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  sampleQuestions: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flexWrap: "wrap",
  },
  sampleLabel: {
    fontSize: 10,
    color: "var(--muted, #8A7A66)",
  },
  sampleChip: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 4,
    color: "var(--gold, #F9AF36)",
    fontSize: 10,
    padding: "2px 6px",
    cursor: "pointer",
  },
};

